import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminService from '../../../services/adminService';

const UrgentDeadlinesPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [sortBy, setSortBy] = useState('deadline'); // 'deadline' | 'urgency'

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const { data, error: err } = await AdminService.getUnifiedDeadlines({
        startDate,
        endDate,
        alertLevels: ['critical', 'urgent'],
        deadlineTypes: ['task', 'university_application', 'visa_application', 'agreement']
      });

      if (err) {
        setError(err);
      } else {
        setRows(data || []);
      }
      setLoading(false);
    };
    load();
  }, []);

  const filteredRows = rows.filter((row) => {
    const matchesType = typeFilter === 'all' || row.deadline_type === typeFilter;
    const matchesAssignee = !assigneeFilter || (row.assigned_to || '').toLowerCase().includes(assigneeFilter.toLowerCase());
    return matchesType && matchesAssignee;
  });

  const sortedRows = [...filteredRows].sort((a, b) => {
    if (sortBy === 'urgency') {
      const aScore = a.urgency_score ?? 0;
      const bScore = b.urgency_score ?? 0;
      return bScore - aScore;
    }
    const aDate = a.deadline ? new Date(a.deadline).getTime() : Infinity;
    const bDate = b.deadline ? new Date(b.deadline).getTime() : Infinity;
    return aDate - bDate;
  });

  const handleRowClick = (row) => {
    const base = '/admin';
    let path = null;
    if (row.deadline_type === 'task') path = `${base}/tasks/${row.id}`;
    else if (row.deadline_type === 'university_application') path = `${base}/univ_apps/${row.id}`;
    else if (row.deadline_type === 'visa_application') path = `${base}/visa_apps/${row.id}`;
    else if (row.deadline_type === 'agreement') path = `${base}/agreements/${row.id}`;
    if (path) navigate(path);
  };

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Urgent Deadlines (Next 72h)</h1>
          <p className="text-sm text-gray-400">Critical and urgent items across tasks, uni apps, visas, and agreements.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="task">Tasks</option>
            <option value="university_application">University Applications</option>
            <option value="visa_application">Visa Applications</option>
            <option value="agreement">Agreements</option>
          </select>
          <input
            className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm"
            placeholder="Filter by assignee"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          />
          <select
            className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="deadline">Sort: Deadline</option>
            <option value="urgency">Sort: Urgency Score</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-400">Loading urgent deadlines…</div>}
      {error && <div className="text-sm text-red-400">Error: {error}</div>}

      {!loading && !error && (
        <div className="bg-gray-800 border border-gray-700 rounded overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-900 text-gray-400 uppercase text-xs">
              <tr>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Title</th>
                <th className="p-3 text-left">Case</th>
                <th className="p-3 text-left">Assignee</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Deadline</th>
                <th className="p-3 text-left">Alert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-gray-500">
                    No urgent deadlines found.
                  </td>
                </tr>
              )}
              {sortedRows.map((row) => (
                <tr
                  key={`${row.deadline_type}-${row.id}`}
                  className="hover:bg-gray-700/50 cursor-pointer"
                  onClick={() => handleRowClick(row)}
                >
                  <td className="p-3 capitalize">{row.deadline_type.replace('_', ' ')}</td>
                  <td className="p-3 font-medium text-white">{row.title}</td>
                  <td className="p-3 text-gray-300">{row.case_title || '—'}</td>
                  <td className="p-3 text-gray-300">{row.assigned_to || 'Unassigned'}</td>
                  <td className="p-3">
                    <span className="px-2 py-1 rounded text-xs bg-gray-900 border border-gray-700 uppercase">
                      {row.status || '—'}
                    </span>
                  </td>
                  <td className="p-3 text-yellow-300">
                    {row.deadline ? new Date(row.deadline).toLocaleString() : '—'}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs uppercase ${
                      row.alert_level === 'critical' ? 'bg-red-900/50 text-red-300 border border-red-800' :
                      row.alert_level === 'urgent' ? 'bg-orange-900/50 text-orange-300 border border-orange-800' :
                      'bg-yellow-900/50 text-yellow-300 border border-yellow-800'
                    }`}>
                      {row.alert_level || 'attention'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UrgentDeadlinesPage;
